import React, { useMemo, useState } from 'react';
import { useData } from '../../contexts/DataContext';
import {
  Box,
  Typography,
  Button,
  Checkbox,
  TextField,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Stack,
  InputAdornment,
} from '@mui/material';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import SortIcon from '@mui/icons-material/Sort';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PageHeader from '../common/PageHeader';
import ListSection from '../common/ListSection';
import ToolbarButton from '../common/ToolbarButton';
import EmptyState from '../common/EmptyState';
import AppDialog, { DialogField } from '../common/AppDialog';
import ConfirmDialog from '../common/ConfirmDialog';
import ConditionGroupsEditor from '../common/ConditionGroupsEditor';
import ScrollableFilters from '../Rules/components/ScrollableFilters';
import { useScrollableFilters } from '../Rules/hooks/useScrollableFilters';
import GoalCard from './GoalCard';
import { goalOutlook } from '../../utils/goalOutlook';

/*
  Goals: a destination, not a fourth object (docs/ACTION-SCHEDULING-DESIGN.md).
  A goal is a target amount by a date, tracked one of two ways:
   - 'account': pinned to an account's balance — on-track / short-by comes
     from the balance projection, and the goal draws its target line on the
     Overview page's Projected Balances chart.
   - 'transactions': counted up from transactions matching rule-style
     condition groups (same engine the Rules page uses) — progress is the
     matched sum, and the outlook is pace-based.
*/

// Same white-background filter fields as the Transactions/Actions toolbars.
const filterFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    backgroundColor: '#fff',
    '&:hover': { backgroundColor: '#fff' },
    '&.Mui-focused': { backgroundColor: '#fff' }
  }
};

const emptyForm = () => ({
  id: '',
  name: '',
  trackingType: 'account',
  accountId: '',
  conditionGroups: [
    { conditions: [{ field: 'description', operator: 'contains', value: '' }] }
  ],
  trackFrom: '',
  countMode: 'net',
  targetAmount: '',
  targetDate: '',
  imageUrl: '',
  notes: '',
});

// Downscale a picked image to a compact JPEG data URL so goal cards stay
// light in Mongo and over the wire.
const fileToDataUrl = (file, maxDim = 900, quality = 0.82) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read the file'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Not a readable image'));
      img.onload = () => {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(img.width * scale));
        canvas.height = Math.max(1, Math.round(img.height * scale));
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

const GoalList = () => {
  const {
    goals, accounts, actions, budgets, transactions,
    addGoal, updateGoal, deleteGoal, getTransactionFields,
  } = useData();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState(emptyForm());
  const [formError, setFormError] = useState('');
  const [goalToDelete, setGoalToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [accountFilter, setAccountFilter] = useState('');
  const [selectedGoalIds, setSelectedGoalIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null);
  const [sortBy, setSortBy] = useState('targetDate');
  const [sortDirection, setSortDirection] = useState('asc');

  const filtersScroll = useScrollableFilters();

  const activeAccounts = useMemo(
    () => accounts.filter((a) => a.isActive !== false),
    [accounts]
  );

  const availableFields = useMemo(() => getTransactionFields(), [getTransactionFields]);

  // On-track / short-by for every goal — balance projection for account
  // goals, matched-transaction pace for pattern goals.
  const outlooks = useMemo(() => {
    const map = {};
    (goals || []).forEach((goal) => {
      map[goal.id] = goalOutlook({ goal, accounts, actions, budgets, transactions });
    });
    return map;
  }, [goals, accounts, actions, budgets, transactions]);

  const filteredGoals = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const list = (goals || []).filter((g) => {
      const termOk =
        !term ||
        (g.name && g.name.toLowerCase().includes(term)) ||
        (g.notes && g.notes.toLowerCase().includes(term));
      const accountOk = !accountFilter || g.accountId === accountFilter;
      return termOk && accountOk;
    });
    list.sort((a, b) => {
      let av, bv;
      switch (sortBy) {
        case 'name':
          av = (a.name || '').toLowerCase(); bv = (b.name || '').toLowerCase(); break;
        case 'targetAmount':
          av = Number(a.targetAmount) || 0; bv = Number(b.targetAmount) || 0; break;
        case 'targetDate':
        default:
          av = new Date(a.targetDate).getTime(); bv = new Date(b.targetDate).getTime();
      }
      if (av < bv) return sortDirection === 'asc' ? -1 : 1;
      if (av > bv) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [goals, searchTerm, accountFilter, sortBy, sortDirection]);

  const allSelected =
    filteredGoals.length > 0 && filteredGoals.every((g) => selectedGoalIds.includes(g.id));
  const someSelected = filteredGoals.some((g) => selectedGoalIds.includes(g.id));

  const toggleSelect = (id) => {
    setSelectedGoalIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    setSelectedGoalIds(allSelected ? [] : filteredGoals.map((g) => g.id));
  };

  const openDialog = (goal = null) => {
    if (goal) {
      setFormData({
        id: goal.id,
        name: goal.name || '',
        trackingType: goal.trackingType === 'transactions' ? 'transactions' : 'account',
        accountId: goal.accountId || '',
        conditionGroups:
          goal.conditionGroups && goal.conditionGroups.length > 0
            ? goal.conditionGroups.map((g) => ({
                conditions: (g.conditions || []).map((c) => ({
                  field: c.field, operator: c.operator, value: c.value,
                })),
              }))
            : emptyForm().conditionGroups,
        trackFrom: goal.trackFrom ? String(goal.trackFrom).slice(0, 10) : '',
        countMode: goal.countMode === 'absolute' ? 'absolute' : 'net',
        targetAmount: goal.targetAmount ?? '',
        targetDate: goal.targetDate ? String(goal.targetDate).slice(0, 10) : '',
        imageUrl: goal.imageUrl || '',
        notes: goal.notes || '',
      });
      setEditMode(true);
    } else {
      setFormData(emptyForm());
      setEditMode(false);
    }
    setFormError('');
    setDialogOpen(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    try {
      const dataUrl = await fileToDataUrl(file);
      setFormData((prev) => ({ ...prev, imageUrl: dataUrl }));
      setFormError('');
    } catch (err) {
      setFormError('Could not read that image. Try a different file.');
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) return setFormError('Goal name is required');
    const isPattern = formData.trackingType === 'transactions';
    if (!isPattern && !formData.accountId) {
      return setFormError('Pick the account this goal is pinned to');
    }
    if (isPattern) {
      const hasEmpty = formData.conditionGroups.some((g) =>
        (g.conditions || []).some((c) => !String(c.value || '').trim())
      );
      if (!formData.conditionGroups.length || hasEmpty) {
        return setFormError('All matching conditions must have values');
      }
    }
    const amount = parseFloat(formData.targetAmount);
    if (!Number.isFinite(amount) || amount <= 0) return setFormError('Target amount must be a positive number');
    if (!formData.targetDate) return setFormError('Target date is required');

    const goalData = {
      name: formData.name.trim(),
      trackingType: formData.trackingType,
      accountId: isPattern ? '' : formData.accountId,
      conditionGroups: isPattern ? formData.conditionGroups : [],
      trackFrom: isPattern && formData.trackFrom ? new Date(formData.trackFrom).toISOString() : null,
      countMode: formData.countMode,
      targetAmount: amount,
      targetDate: new Date(formData.targetDate).toISOString(),
      imageUrl: formData.imageUrl || '',
      notes: formData.notes,
      isActive: true,
    };
    try {
      if (editMode) {
        await updateGoal(formData.id, goalData);
      } else {
        await addGoal(goalData);
      }
      setDialogOpen(false);
    } catch (err) {
      setFormError('Could not save the goal. Please try again.');
    }
  };

  const handleBulkDelete = async () => {
    for (const id of selectedGoalIds) {
      if (!id) continue;
      await deleteGoal(id);
    }
    setSelectedGoalIds([]);
    setBulkDeleteOpen(false);
  };

  const accountFor = (goal) => accounts.find((a) => a.id === goal.accountId);

  const isPatternForm = formData.trackingType === 'transactions';

  return (
    <Box sx={{ width: '100%' }}>
      <PageHeader title="Goals" icon={<FlagOutlinedIcon fontSize="large" />} />
      {/* Overflow guard sits below the header so it can't clip the
          full-bleed negative margins */}
      <Box sx={{ overflow: 'hidden' }}>

        <ListSection
          actions={[
            <ToolbarButton key="add" startIcon={<AddIcon />} onClick={() => openDialog()}>
              Add
            </ToolbarButton>,
            // Delete only appears once cards are ticked (via their
            // hover checkboxes), matching the Budgets page.
            ...(selectedGoalIds.length
              ? [
                  <ToolbarButton
                    key="bulkDelete"
                    startIcon={<DeleteIcon />}
                    onClick={() => setBulkDeleteOpen(true)}
                  >
                    Delete ({selectedGoalIds.length})
                  </ToolbarButton>
                ]
              : [])
          ]}
          filters={
            <ScrollableFilters
              scrollRef={filtersScroll.scrollRef}
              showLeftScroll={filtersScroll.showLeftScroll}
              showRightScroll={filtersScroll.showRightScroll}
              onScrollLeft={filtersScroll.scrollLeft}
              onScrollRight={filtersScroll.scrollRight}
            >
              <TextField
                label="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                sx={{ minWidth: 200, ...filterFieldSx }}
                InputProps={{
                  startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
                  style: { fontSize: '0.875rem' }
                }}
              />
              <FormControl size="small" sx={{ minWidth: 220, ...filterFieldSx }}>
                <InputLabel id="goal-filter-account-label" shrink>Account</InputLabel>
                <Select
                  labelId="goal-filter-account-label"
                  value={accountFilter}
                  label="Account"
                  onChange={(e) => setAccountFilter(e.target.value)}
                  displayEmpty
                  sx={{ fontSize: '0.875rem' }}
                >
                  <MenuItem value="" sx={{ fontSize: '0.875rem' }}>All Accounts</MenuItem>
                  {accounts.map((acc) => (
                    <MenuItem key={acc.id} value={acc.id} sx={{ fontSize: '0.875rem' }}>{acc.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </ScrollableFilters>
          }
        >
          {filteredGoals.length === 0 ? (
            <EmptyState
              icon={<FlagOutlinedIcon />}
              title={goals && goals.length ? 'No goals match' : 'No goals yet'}
              description={
                goals && goals.length
                  ? 'Try changing your search or account filter.'
                  : 'Pin a target to an account, like "$10,000 in the Japan fund by March", or count up transactions matching a pattern. The outlook will tell you whether you\'re on track.'
              }
              action={
                !(goals && goals.length) && (
                  <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDialog()}>
                    Add a goal
                  </Button>
                )
              }
            />
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, minHeight: 32 }}>
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected && !allSelected}
                  onChange={toggleSelectAll}
                  size="small"
                  sx={{ p: 0 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>
                  Showing {filteredGoals.length} goal{filteredGoals.length !== 1 ? 's' : ''}
                </Typography>
                <Box sx={{ ml: 'auto' }}>
                  <Button size="small" startIcon={<SortIcon />} onClick={(e) => setSortMenuAnchor(e.currentTarget)} sx={{ textTransform: 'none', fontSize: '0.8125rem', fontWeight: 500 }}>
                    Sort by
                  </Button>
                  <Menu anchorEl={sortMenuAnchor} open={Boolean(sortMenuAnchor)} onClose={() => setSortMenuAnchor(null)} keepMounted>
                    <MenuItem sx={{ fontSize: '0.8125rem' }} onClick={() => { setSortBy('name'); setSortDirection('asc'); setSortMenuAnchor(null); }}>Name ↑</MenuItem>
                    <MenuItem sx={{ fontSize: '0.8125rem' }} onClick={() => { setSortBy('name'); setSortDirection('desc'); setSortMenuAnchor(null); }}>Name ↓</MenuItem>
                    <MenuItem sx={{ fontSize: '0.8125rem' }} onClick={() => { setSortBy('targetDate'); setSortDirection('asc'); setSortMenuAnchor(null); }}>Target Date ↑</MenuItem>
                    <MenuItem sx={{ fontSize: '0.8125rem' }} onClick={() => { setSortBy('targetDate'); setSortDirection('desc'); setSortMenuAnchor(null); }}>Target Date ↓</MenuItem>
                    <MenuItem sx={{ fontSize: '0.8125rem' }} onClick={() => { setSortBy('targetAmount'); setSortDirection('asc'); setSortMenuAnchor(null); }}>Amount ↑</MenuItem>
                    <MenuItem sx={{ fontSize: '0.8125rem' }} onClick={() => { setSortBy('targetAmount'); setSortDirection('desc'); setSortMenuAnchor(null); }}>Amount ↓</MenuItem>
                  </Menu>
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gap: 1.75,
                  alignItems: 'start',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(auto-fill, minmax(255px, 1fr))',
                    md: 'repeat(auto-fill, minmax(290px, 1fr))'
                  }
                }}
              >
                {filteredGoals.map((goal) => (
                  <GoalCard
                    key={goal.id}
                    goal={goal}
                    account={accountFor(goal)}
                    outlook={outlooks[goal.id]}
                    onEdit={openDialog}
                    onDelete={setGoalToDelete}
                    selectable
                    selected={selectedGoalIds.includes(goal.id)}
                    onToggleSelect={toggleSelect}
                  />
                ))}
              </Box>
            </>
          )}
        </ListSection>
      </Box>

      {/* Add / Edit dialog */}
      <AppDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editMode ? 'Edit Goal' : 'Add Goal'}
        subtitle="A target amount by a date"
        primaryLabel={editMode ? 'Save' : 'Add'}
        onPrimary={handleSubmit}
      >
        {formError && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {formError}
          </Typography>
        )}
        <DialogField label="Name" required>
          <TextField
            fullWidth
            size="small"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="e.g. Japan trip"
            autoFocus
          />
        </DialogField>

        <DialogField
          label="Track progress by"
          helper={
            isPatternForm
              ? 'Progress is the sum of transactions matching the conditions below, using the same matching as the Rules page.'
              : "Progress is the account's balance, with the on-track outlook from the balance projection."
          }
        >
          <TextField
            fullWidth
            select
            size="small"
            name="trackingType"
            value={formData.trackingType}
            onChange={handleChange}
          >
            <MenuItem value="account">Account balance</MenuItem>
            <MenuItem value="transactions">Matching transactions (pattern)</MenuItem>
          </TextField>
        </DialogField>

        {!isPatternForm && (
          <DialogField label="Account" required>
            <TextField
              fullWidth
              select
              size="small"
              name="accountId"
              value={formData.accountId}
              onChange={handleChange}
            >
              {activeAccounts.map((account) => (
                <MenuItem key={account.id} value={account.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: account.color || 'grey.400' }} />
                    {account.name}
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          </DialogField>
        )}

        {isPatternForm && (
          <>
            <DialogField label="Matching Conditions" required>
              <ConditionGroupsEditor
                groups={formData.conditionGroups}
                onChange={(conditionGroups) => setFormData((prev) => ({ ...prev, conditionGroups }))}
                availableFields={availableFields}
              />
            </DialogField>
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <DialogField
                label="Count from"
                helper="Only count matches on or after this date. Leave empty for all history."
                sx={{ mt: 0, flex: 1, minWidth: 0 }}
              >
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  name="trackFrom"
                  value={formData.trackFrom}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
              </DialogField>
              <DialogField
                label="Counting"
                helper={
                  formData.countMode === 'absolute'
                    ? 'Every match adds its amount, ignoring the sign (useful for loan repayments).'
                    : 'Deposits add, withdrawals subtract.'
                }
                sx={{ mt: 0, flex: 1, minWidth: 0 }}
              >
                <TextField
                  fullWidth
                  select
                  size="small"
                  name="countMode"
                  value={formData.countMode}
                  onChange={handleChange}
                >
                  <MenuItem value="net">Net amount</MenuItem>
                  <MenuItem value="absolute">Every match counts</MenuItem>
                </TextField>
              </DialogField>
            </Box>
          </>
        )}

        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <DialogField label="Target amount" required sx={{ mt: 0, flex: 1, minWidth: 0 }}>
            <TextField
              fullWidth
              size="small"
              type="number"
              name="targetAmount"
              value={formData.targetAmount}
              onChange={handleChange}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            />
          </DialogField>
          <DialogField label="Target date" required sx={{ mt: 0, flex: 1, minWidth: 0 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              name="targetDate"
              value={formData.targetDate}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
          </DialogField>
        </Box>

        <DialogField label="Image" helper="Shown as a banner on the goal card: a picture of what you're saving for.">
          {formData.imageUrl && (
            <Box
              sx={{
                height: 110,
                mb: 1,
                borderRadius: 2,
                backgroundImage: `url(${formData.imageUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                border: '1px solid',
                borderColor: 'divider',
              }}
            />
          )}
          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            <Button
              component="label"
              size="small"
              variant="outlined"
              startIcon={<ImageOutlinedIcon />}
              sx={{ textTransform: 'none' }}
            >
              {formData.imageUrl ? 'Change image' : 'Upload image'}
              <input type="file" accept="image/*" hidden onChange={handleImageFile} />
            </Button>
            {formData.imageUrl && (
              <Button
                size="small"
                color="inherit"
                sx={{ textTransform: 'none', color: 'text.secondary' }}
                onClick={() => setFormData((prev) => ({ ...prev, imageUrl: '' }))}
              >
                Remove
              </Button>
            )}
          </Stack>
          <TextField
            fullWidth
            size="small"
            name="imageUrl"
            value={formData.imageUrl.startsWith('data:') ? '' : formData.imageUrl}
            onChange={handleChange}
            placeholder="…or paste an image URL"
          />
        </DialogField>

        <DialogField label="Notes">
          <TextField
            fullWidth
            size="small"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            multiline
            rows={2}
          />
        </DialogField>
      </AppDialog>

      {/* Delete confirmation (single) */}
      <ConfirmDialog
        open={Boolean(goalToDelete)}
        onClose={() => setGoalToDelete(null)}
        title="Delete Goal"
        message={`Delete "${goalToDelete?.name}"? The account and any transfer actions are untouched. Only the milestone marker goes.`}
        confirmLabel="Delete"
        onConfirm={async () => {
          if (goalToDelete) {
            await deleteGoal(goalToDelete.id);
            setSelectedGoalIds((prev) => prev.filter((id) => id !== goalToDelete.id));
          }
          setGoalToDelete(null);
        }}
      />

      {/* Delete confirmation (bulk) */}
      <ConfirmDialog
        open={bulkDeleteOpen}
        onClose={() => setBulkDeleteOpen(false)}
        title="Delete Goals"
        message={`Delete ${selectedGoalIds.length} selected goal${selectedGoalIds.length !== 1 ? 's' : ''}? Accounts and transfer actions are untouched. Only the milestone markers go.`}
        confirmLabel="Delete"
        onConfirm={handleBulkDelete}
      />
    </Box>
  );
};

export default GoalList;
